import React, { useState, FormEvent, ChangeEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { saveAuthInfo, type AuthInfo } from '@/utils/auth'
import { bootstrapProjectContext, clearSession } from '@/utils/session'
import { getApiV1Url } from '@/config/api'
import styles from './Login.module.css'

const Login: React.FC = () => {
  const navigate = useNavigate()
  const [projectCode, setProjectCode] = useState('')
  const [outletCode, setOutletCode] = useState('')
  const [password, setPassword] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    if (name === 'projectCode') setProjectCode(value)
    if (name === 'outletCode') setOutletCode(value)
    if (name === 'password') setPassword(value)
    setError('')
  }

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!projectCode.trim()) {
      setError('프로젝트 코드를 입력해주세요.')
      return
    }
    if (!outletCode.trim()) {
      setError('매장 아이디를 입력해주세요.')
      return
    }
    if (!password) {
      setError('비밀번호를 입력해주세요.')
      return
    }

    setIsLoading(true)
    setError('')
    try {
      const response = await fetch(getApiV1Url(`/projects/${projectCode.trim()}/shops/auth`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ outletCode: outletCode.trim(), loginPassword: password })
      })

      const data = await response.json()

      if (response.ok && data?.data?.isAuthenticated) {
        const trimmedProjectCode = projectCode.trim()
        const authInfo: AuthInfo = {
          projectCode: trimmedProjectCode,
          outletCode: outletCode.trim(),
          shopCode: data.data.shopCode ?? '',
          shopName: data.data.shopName ?? '',
          brandCode: data.data.brandCode ?? '',
          isAuthenticated: true
        }
        saveAuthInfo(authInfo)
        try {
          await bootstrapProjectContext(trimmedProjectCode)
          navigate('/dashboard')
        } catch (lookupErr) {
          clearSession()
          setError(
            lookupErr instanceof Error
              ? lookupErr.message
              : '프로젝트 정보를 불러오지 못했습니다. 다시 시도해주세요.'
          )
        }
      } else {
        const msg =
          data?.errors?.error?.[0]?.message ||
          data?.message ||
          '아이디 또는 비밀번호가 올바르지 않습니다.'
        setError(msg)
      }
    } catch {
      setError('로그인에 실패했습니다. 다시 시도해주세요.')
    } finally {
      setIsLoading(false)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, nextId: string) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      document.getElementById(nextId)?.focus()
    }
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.loginContainer}>
        <div className={styles.logoSection}>
          <div className={styles.logo}>S</div>
          <h1 className={styles.title}>매장 에이전트</h1>
          <p className={styles.subtitle}>매장 관리 시스템에 로그인하세요</p>
        </div>

        {error && (
          <div className={styles.errorMessage} role="alert">
            {error}
          </div>
        )}

        <form id="loginForm" onSubmit={handleSubmit}>
          <div className={styles.formGroup}>
            <label htmlFor="projectCode" className={styles.label}>
              프로젝트 코드
            </label>
            <input
              type="text"
              id="projectCode"
              name="projectCode"
              className={styles.input}
              placeholder="프로젝트 코드를 입력하세요"
              value={projectCode}
              onChange={handleChange}
              onKeyDown={(e) => handleKeyDown(e, 'outletCode')}
              required
              autoComplete="off"
            />
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="outletCode" className={styles.label}>
              매장 아이디
            </label>
            <input
              type="text"
              id="outletCode"
              name="outletCode"
              className={styles.input}
              placeholder="매장 아이디를 입력하세요"
              value={outletCode}
              onChange={handleChange}
              onKeyDown={(e) => handleKeyDown(e, 'login-password')}
              required
              autoComplete="username"
            />
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="password" className={styles.label}>
              비밀번호
            </label>
            <input
              type="password"
              id="login-password"
              name="password"
              className={styles.input}
              placeholder="비밀번호를 입력하세요"
              value={password}
              onChange={handleChange}
              required
              autoComplete="current-password"
            />
          </div>

          <button type="submit" className={styles.loginButton} disabled={isLoading}>
            {isLoading ? '로그인 중...' : '로그인'}
          </button>
        </form>
      </div>
    </div>
  )
}

export default Login
